import { get, isFunction } from 'lodash-es';
import * as z from 'zod';
import * as ba from './base';
import { isComponentContext, assertComponentContext } from '../helpers';
import { MODAL_RESULT_SCHEMA } from './modal';
import { IModalResult } from '../modal';
import { isComponentValueCache } from '../caching';

export interface CloseComponentActionArgs {
  result: IModalResult | any;
}

export class CloseComponentAction extends ba.Action<
  CloseComponentActionArgs,
  any
> {
  static get manifest() {
    return {
      name: 'closeComponent',
      schema: z.object({ result: MODAL_RESULT_SCHEMA }).optional(),
    };
  }
  async handle(event: ba.ActionEvent, args: CloseComponentActionArgs) {
    const component = event.component;

    if (isComponentContext(component)) {
      if (component.isModalComponent && component.modalName) {
        return component.$imodal.close(
          component.modalName,
          (args ? args.result : null) || { cancel: true },
        );
        // component.$imodal.close(component.modalName, args.result);
      } else {
        const fn = get(component, '$tabs.close');
        if (isFunction(fn)) {
          (fn as Function)();
        }
      }
    }
  }
}

export interface ReloadComponentDataActionArgs {
  value?: string;
}

export class ReloadComponentDataAction extends ba.Action<
  ReloadComponentDataActionArgs,
  any
> {
  static get manifest() {
    return {
      name: 'reloadComponentData',
      schema: z
        .object({
          value: z.string().optional(),
        })
        .optional(),
    };
  }

  async handle(
    event: ba.ActionEvent,
    args: ReloadComponentDataActionArgs | null,
  ) {
    const component = event.component;
    assertComponentContext(component);
    if (args?.value) {
      component.loadData(args?.value);
    } else {
      component.loadData();
    }
  }
}

export interface SetDataActionArgs {
  path: string;
  value: any;
  resolve?: boolean;
  noTouchLastLoaded?: boolean;
  noTakeSnapshot?: boolean;
}

export class SetDataAction extends ba.Action<SetDataActionArgs, any> {
  static get manifest() {
    return {
      name: 'setData',
      schema: z.object({
        path: z.string(),
        value: z.any(),
        resolve: z.boolean().optional().default(false),
        noTouchLastLoaded: z.boolean().optional().default(false),
        noTakeSnapshot: z.boolean().optional().default(false),
      }),
    };
  }

  async handle(event: ba.ActionEvent, args: SetDataActionArgs) {
    const component = event.component;
    assertComponentContext(component);

    const value = args.resolve ? Promise.resolve(args.value) : args.value;

    component.setData(args.path, value, {
      noTouchLastLoaded: args.noTouchLastLoaded || false,
      noTakeSnapshot: args.noTakeSnapshot || false,
    });
  }
}

export interface CacheActionArgs {
  path: string;
}

export class CacheAction extends ba.Action<ClearCacheActionArgs, any> {
  static get manifest() {
    return {
      name: 'cache',
      schema: z.object({
        path: z.string(),
      }),
    };
  }

  async handle(event: ba.ActionEvent, args: ClearCacheActionArgs) {
    const component = event.component;
    assertComponentContext(component);

    const handler: any = get(component, `$$cache_${args.path}`, null);
    if (isComponentValueCache(handler)) {
      await handler.persist(get(component, args.path));
    }
  }
}

export interface ClearCacheActionArgs {
  path: string;
}

export class ClearCacheAction extends ba.Action<ClearCacheActionArgs, any> {
  static get manifest() {
    return {
      name: 'clearCache',
      schema: z.object({
        path: z.string(),
      }),
    };
  }

  async handle(event: ba.ActionEvent, args: ClearCacheActionArgs) {
    const component = event.component;
    assertComponentContext(component);
    const handler: any = get(component, `$$cache_${args.path}`);
    if (isComponentValueCache(handler)) {
      await handler.clearCache();
    }
  }
}
